import {
  type FC, memo, useCallback, useEffect, useMemo, useState,
} from 'react';
import { useDispatch } from 'react-redux';
import { type LatLng } from '~/_shared/types/latLng';
import { type IndividualRadiusProximity } from '~/_shared/types/proximity/proximity.types';
import { useIsMapInteractionActive } from '~/_shared/utils/hooks/useIsMapInteractionActive';
import { useMapObjectDragAndDrop } from '~/_shared/utils/hooks/useMapObjectDragAndDrop';
import { useSelector } from '~/_shared/utils/hooks/useSelector';
import { changeIndividualProximityLatLng } from '~/_shared/utils/proximity';
import { ActiveProximityCenterHighlightContainer } from '~/map/map/proximity/activeProximityCenterHighlight.container';
import { ActiveProximityHighlightContainer } from '~/map/map/proximity/activeProximityHighlight.container';
import { HoveredProximityHighlightContainer } from '~/map/map/proximity/hoveredProximityHighlight.container';
import { useCenterInteraction } from '~/map/map/proximity/useCenterMarkerInteraction';
import { useProximityStates } from '~/map/map/proximity/useProximityStates';
import {
  endIndividualRadiusDrag, endIndividualRadiusHoverInDnD, startIndividualRadiusDnDMode, startIndividualRadiusDrag,
  startIndividualRadiusHoverInDnD,
} from '~/store/frontendState/proximityTool/proximityTool.actionCreators';
import { useProximityToolSelector } from '~/store/frontendState/proximityTool/proximityTool.selectors';
import { moveIndividualProximity } from '~/store/mapSettings/proximity/mapSettingsProximity.actionCreators';
import { useIndividualOrDriveTimeProximityZIndex } from '../../zIndexes/useIndividualOrDriveTimeProximityZIndex.hook';
import { isProximityBigEnough } from './isProximityBigEnough.helper';
import { type MapProximityManager } from './mapProximityManager';

type ProximityInstanceCircleIndividualProps = {
  isVisible: boolean;
  manager: MapProximityManager;
  map: google.maps.Map | undefined;
  proximity: IndividualRadiusProximity;
  zoom: number;
};

export const ProximityInstanceCircleIndividual: FC<ProximityInstanceCircleIndividualProps> = memo(({
  isVisible,
  manager,
  map,
  proximity,
  zoom,
}) => {
  const hideLabel = useSelector(s => s.map.mapSettings.data.proximity.hideLabels);
  const hideSmallRadii = useSelector(s => s.map.mapSettings.data.proximity.hideSmallRadii);
  const proximityIdInDnD = useProximityToolSelector().proximityIdInDnDMode;
  const zIndex = useIndividualOrDriveTimeProximityZIndex(proximity.id, 'circle');
  const isMapInteractionActive = useIsMapInteractionActive();
  const dispatch = useDispatch();
  const { isActive, isHovered } = useProximityStates(proximity);

  const [isInDnDMode, setIsInDnDMode] = useState(false);
  const [isMouseOverProximity, setIsMouseOverProximity] = useState(false);
  const [latestLatLng, setLatestLatLng] = useState<LatLng>(proximity.data.data);
  const [renderedCircle, setRenderedCircle] = useState<WebglOverlayCircleArea | null>(null);
  const [renderedCenter, setRenderedCenter] = useState<WebglOverlayMarker | null>(null);
  const [dragStartToCenterVector, setDragStartToCenterVector] = useState<LatLng>({ lat: 0, lng: 0 });

  const { isMouseOverCenter } = useCenterInteraction({ renderedCenter, proximityId: proximity.id });

  // we combine hideSmallRadii and zoom to one variable
  // so when hideSmallRadii is not enabled, we don't do unnecessary processing
  const hideSmallRadiiZoom = useMemo(() => (
    hideSmallRadii ? zoom : null
  ), [hideSmallRadii, zoom]);

  const startDnDMode = useCallback(() => {
    dispatch(startIndividualRadiusDnDMode(proximity.id));
    setIsMouseOverProximity(true);
  }, [dispatch, proximity.id]);

  const endDnDMode = useCallback(() => {
    dispatch(moveIndividualProximity(proximity.id, latestLatLng));
  }, [dispatch, proximity.id, latestLatLng]);

  const onMouseEnteredProximity = useCallback(() => {
    if (isInDnDMode) {
      dispatch(startIndividualRadiusHoverInDnD());
    }
    setIsMouseOverProximity(true);
  }, [isInDnDMode, dispatch]);
  const onMouseLeftProximity = useCallback(() => {
    if (isInDnDMode) {
      dispatch(endIndividualRadiusHoverInDnD());
    }
    setIsMouseOverProximity(false);
  }, [isInDnDMode, dispatch]);

  useMapObjectDragAndDrop(() => ({
    dragDisabled: !isInDnDMode,
    map,
    mapObject: renderedCircle ?? undefined,
    onDragMove: latLng => setLatestLatLng(sumLatLngs(latLng, dragStartToCenterVector)),
    onDragStart: latLng => {
      setDragStartToCenterVector(subtractLatLngs(latestLatLng, latLng));
      dispatch(startIndividualRadiusDrag());
    },
    onDragEnd: () => dispatch(endIndividualRadiusDrag()),
    onMouseOver: onMouseEnteredProximity,
    onMouseOut: onMouseLeftProximity,
  }),
  [isInDnDMode, map, renderedCircle, dispatch, latestLatLng, dragStartToCenterVector, onMouseEnteredProximity,
    onMouseLeftProximity]);

  // Stop / start dnd once redux property changes
  useEffect(() => {
    if (proximityIdInDnD === proximity.id && !isInDnDMode) {
      setIsInDnDMode(true);
    }

    if (proximityIdInDnD !== proximity.id && isInDnDMode) {
      endDnDMode();
      setIsInDnDMode(false);
    }
  }, [proximityIdInDnD, proximity.id, endDnDMode, isInDnDMode]);

  useEffect(
    () => {
      if (hideSmallRadiiZoom !== null && !isProximityBigEnough(proximity.data, hideSmallRadiiZoom)) {
        manager.removeProximityCircle(proximity.id);
      }
      else {
        const proximityWithUpdatedLatLng = changeIndividualProximityLatLng(proximity, latestLatLng);
        const rendered = manager.renderProximitySingleRadius(
          proximityWithUpdatedLatLng,
          isVisible,
          zIndex,
          hideLabel,
          isInDnDMode,
          isMouseOverCenter,
        );

        setRenderedCircle(rendered?.circle ?? null);
        setRenderedCenter(rendered?.centerMarker ?? null);
      }
    },
    [manager, isVisible, proximity, zIndex, hideLabel, hideSmallRadiiZoom, latestLatLng, isInDnDMode,
      isMouseOverCenter],
  );

  useEffect(() => {
    if (isInDnDMode) {
      const clickCallback = () => !isMouseOverProximity && endDnDMode();
      document.addEventListener('click', clickCallback);

      return () => document.removeEventListener('click', clickCallback);
    }
    else {
      const doubleClickCallback = () => isMouseOverCenter && !isMapInteractionActive && startDnDMode();
      document.addEventListener('dblclick', doubleClickCallback);

      return () => document.removeEventListener('dblclick', doubleClickCallback);
    }
  }, [isInDnDMode, isMouseOverProximity, endDnDMode, isMouseOverCenter, startDnDMode, isMapInteractionActive]);

  // Unmount cleanup
  useEffect(() => {
    return () => {
      manager.removeProximityCircle(proximity.id);
    };
  }, [manager, proximity.id]);

  return (
    <>
      {isActive && latestLatLng && (
        <ActiveProximityHighlightContainer
          rowId={proximity.data.data.rowId}
          coordinates={latestLatLng}
          proximity={proximity}
        />
      )}
      {isActive && latestLatLng && (
        <ActiveProximityCenterHighlightContainer
          center={latestLatLng}
          map={map}
        />
      )}
      {isHovered && latestLatLng && (
        <HoveredProximityHighlightContainer
          rowId={proximity.data.data.rowId}
          coordinates={latestLatLng}
          proximity={proximity}
        />
      )}
    </>
  );
});

const combineLatLngs = (latLng1: LatLng, latLng2: LatLng, combinator: (coordinate1: number, coordinate2: number) => number) => ({
  lat: combinator(latLng1.lat, latLng2.lat),
  lng: combinator(latLng1.lng, latLng2.lng),
});

const sumLatLngs = (latLng1: LatLng, latLng2: LatLng) => combineLatLngs(latLng1, latLng2, (a, b) => a + b);

const subtractLatLngs = (latLng1: LatLng, latLng2: LatLng) => combineLatLngs(latLng1, latLng2, (a, b) => a - b);
